import { useConfig } from '../../../../hooks/useConfig';

/** 模拟数据预设：用于快速填充场景任务配置与问题输入。 */
export interface MockPresetVariable {
  key: string;
  type: 'number' | 'string';
  value: string | number | null;
}

export type MockPresetKey = 'story' | 'meddicc' | 'experimentReport';

export interface MockPreset {
  key: MockPresetKey;
  label: string;
  scenarioOntologyId?: number | null;
  scenarioOntologyName?: string | null;
  objectOntologyId?: number | null;
  objectOntologyName?: string | null;
  knowledgeBaseId?: string | null;
  knowledgeBaseName?: string | null;
  variables: MockPresetVariable[];
  process: Record<string, unknown>;
  question: string;
}

const STORY_PROCESS = {
  name: '编写汽车制造行业AI场景故事',
  concurrency: '串行',
  agents: [
    '市场专员',
    '汽车制造业专家',
    '技术专家',
    '场景故事编辑',
    '责任编辑',
  ],
  instruments: [
    '用户问题',
    '本体ID',
    '知识库ID',
    '最小资料数量',
    '每次收集条数',
  ],
  constructions: ['场景故事'],
  processes: [
    {
      name: '解析故事编写需求',
      concurrency: '串行',
      agents: ['市场专员'],
      instruments: ['用户问题'],
      constructions: ['故事编写需求'],
    },
    {
      name: '获取零件资料',
      concurrency: '并行',
      agents: ['汽车制造业专家'],
      instruments: ['故事编写需求', '本体ID', '知识库ID'],
      constructions: ['零件资料'],
      processes: [
        {
          name: '获取零件重量范围',
          concurrency: '并行',
          agents: ['汽车制造业专家'],
          instruments: ['故事编写需求', '本体ID'],
          constructions: ['零件重量范围'],
        },
        {
          name: '获取零件BOM结构',
          concurrency: '并行',
          agents: ['汽车制造业专家'],
          instruments: ['故事编写需求', '知识库ID'],
          constructions: ['零件BOM结构'],
        },
        {
          name: '整理零件资料',
          concurrency: '串行',
          instruments: ['零件重量范围', '零件BOM结构'],
          constructions: ['零件资料'],
        },
      ],
    },
    {
      name: '收集行业资料',
      concurrency: '并行',
      agents: ['汽车制造业专家', '技术专家'],
      instruments: ['故事编写需求', '最小资料数量', '每次收集条数'],
      constructions: ['行业资料'],
      processes: [
        {
          name: '判断有效资料列表是否达到要求',
          concurrency: '串行',
          instruments: ['有效资料列表', '最小资料数量'],
          constructions: ['判断结果'],
        },
        {
          name: '收集资料',
          concurrency: '串行',
          agents: ['汽车制造业专家'],
          instrumentConditions: { 判断结果: '未达到' },
          instruments: ['故事编写需求', '每次收集条数', '有效资料列表'],
          constructions: ['资料列表'],
        },
        {
          name: '评估资料',
          concurrency: '递归',
          agents: ['技术专家'],
          instruments: ['资料列表', '故事编写需求'],
          effects: ['有效资料列表'],
        },
        {
          name: '整理资料',
          concurrency: '串行',
          instrumentConditions: { 判断结果: '已达到' },
          instruments: ['有效资料列表'],
          constructions: ['行业资料'],
        },
      ],
    },
    {
      name: '编写稿件',
      concurrency: '串行',
      agents: ['技术专家', '场景故事编辑', '责任编辑'],
      instruments: ['故事编写需求', '行业资料', '零件资料'],
      constructions: ['场景故事'],
      processes: [
        {
          name: '编写AI场景故事',
          concurrency: '串行',
          agents: ['场景故事编辑'],
          instruments: ['故事编写需求', '行业资料', '零件资料'],
          constructions: ['场景故事'],
        },
        {
          name: '稿件技术验收',
          concurrency: '串行',
          agents: ['技术专家', '场景故事编辑'],
          effects: ['场景故事'],
          processes: [
            {
              name: '技术验收',
              concurrency: '串行',
              agents: ['技术专家'],
              instruments: ['场景故事'],
              constructions: ['修改意见'],
            },
            {
              name: '修改稿件',
              concurrency: '串行',
              agents: ['场景故事编辑'],
              instruments: ['修改意见'],
              effects: ['场景故事'],
            },
          ],
        },
        {
          name: '稿件审核',
          concurrency: '串行',
          agents: ['责任编辑', '场景故事编辑'],
          effects: ['场景故事'],
          processes: [
            {
              name: '审核稿件',
              concurrency: '串行',
              agents: ['责任编辑'],
              instruments: ['场景故事'],
              constructions: ['修改意见'],
            },
            {
              name: '修改稿件',
              concurrency: '递归',
              agents: ['场景故事编辑'],
              instrumentConditions: { 修改意见: '有' },
              instruments: ['修改意见'],
              effects: ['场景故事'],
            },
            {
              name: '拟定标题',
              concurrency: '串行',
              agents: ['责任编辑'],
              instrumentConditions: { 修改意见: '无' },
              effects: ['场景故事'],
            },
          ],
        },
      ],
    },
  ],
} as const;

const MEDDICC_PROCESS = {
  name: '商机评分',
  concurrency: '串行',
  instruments: ['用户问题', '本体ID', '客户名称', '项目名称'],
  constructions: ['总分和每项得分'],
  processes: [
    {
      name: '解析用户问题',
      autonomyAction: '调用提示词添加对象列表到列表',
      autonomyPrompt:
        '如果用户输入中的“最终得分列表”中包含不为空的JSON列表数据，输出空JSON列表。否则，用户输入中的“用户问题”是JSON列表的字符串形式，将字符串转换成问题对应的JSON列表并输出。',
      autonomyWithTool: false,
      concurrency: '串行',
      instruments: ['用户问题', '最终得分列表'],
      effects: ['问答数据列表'],
    },
    {
      name: '判断问答是否继续',
      autonomyAction: '调用提示词输出对象',
      autonomyPrompt:
        "用户输入中的“问答数据列表”是一组问答列表，如果列表中包含内容，返回继续，否则返回结束。返回值为JSON格式，样式为{'状态': '继续'}，或{'状态': '结束'}。",
      autonomyWithTool: false,
      concurrency: '串行',
      instruments: ['问答数据列表'],
      constructions: ['判断结果'],
    },
    {
      name: '获取一条问答数据',
      autonomyAction: '迭代对象列表',
      concurrency: '串行',
      instrumentConditions: { 判断结果: '继续' },
      effects: ['问答数据列表'],
      constructions: ['问答数据'],
    },
    {
      name: '获取评分标准',
      autonomyAction: '调用提示词输出对象',
      autonomyPrompt:
        '# 角色\n你是一个数据查询助手，请根据用户的查询调用工具获取数据。如果查询结果为空，则只返回“暂无数据”。\n\n# 职责\n1 用户输入中包含本体ID，以及问题和回答。\n2 调用intellix查询对象数据：本体ID为用户输入的本体ID，查询对象为评分标准，筛选条件为属性[问题]的值等于用户输入的问题，只返回第一条。\n\n# 要求\n输出的结果为JSON字典。',
      autonomyWithTool: true,
      concurrency: '串行',
      instruments: ['本体ID', '问答数据'],
      constructions: ['问题评分标准'],
    },
    {
      name: '合并数据',
      autonomyAction: '调用提示词输出对象',
      autonomyPrompt:
        '用户输入的是JSON字典，将问答数据、问题评分标准中的叶子节点的字段都提升到第一层，去掉空内容字段。',
      autonomyWithTool: false,
      concurrency: '串行',
      instruments: ['客户名称', '项目名称', '问答数据', '问题评分标准'],
      constructions: ['问答详细数据'],
    },
    {
      name: '助理1评分',
      autonomyAction: '调用提示词添加对象到列表',
      autonomyPrompt:
        '# 角色\n你是销售商机评分助理1\n\n# 职责\n1 用户输入中包含销售项目的客户名称、项目名称，以及问题和回答。\n2 用户输入中还包含评价问题的采分点。\n3 按照采分点评分标准，给用户对于问题的回答打分并详细给出评分依据，评分依据需要言简意赅。\n\n# 要求\n输出的结果为JSON字典，输出得分和评分依据两个字段，同时把值写入。',
      autonomyWithTool: false,
      concurrency: '并行',
      instruments: ['问答详细数据'],
      effects: ['打分列表'],
    },
    {
      name: '助理2评分',
      autonomyAction: '调用提示词添加对象到列表',
      autonomyPrompt:
        '# 角色\n你是销售商机评分助理2\n\n# 职责\n1 用户输入中包含销售项目的客户名称、项目名称，以及问题和回答。\n2 用户输入中还包含评价问题的采分点。\n3 按照采分点评分标准，给用户对于问题的回答打分并详细给出评分依据，评分依据需要言简意赅。\n\n# 要求\n输出的结果为JSON字典，输出得分和评分依据两个字段，同时把值写入。',
      autonomyWithTool: false,
      concurrency: '并行',
      instruments: ['问答详细数据'],
      effects: ['打分列表'],
    },
    {
      name: '助理3评分',
      autonomyAction: '调用提示词添加对象到列表',
      autonomyPrompt:
        '# 角色\n你是销售商机评分助理3\n\n# 职责\n1 用户输入中包含销售项目的客户名称、项目名称，以及问题和回答。\n2 用户输入中还包含评价问题的采分点。\n3 按照采分点评分标准，给用户对于问题的回答打分并详细给出评分依据，评分依据需要言简意赅。\n\n# 要求\n输出的结果为JSON字典，输出得分和评分依据两个字段，同时把值写入。',
      autonomyWithTool: false,
      concurrency: '并行',
      instruments: ['问答详细数据'],
      effects: ['打分列表'],
    },
    {
      name: '评委评分',
      autonomyAction: '调用提示词添加对象到列表',
      autonomyPrompt:
        '# 角色\n你是销售商机评分员\n\n# 职责\n1 用户输入中包含销售项目的客户名称、项目名称，以及问题和回答。\n2 用户输入中还包含评价问题的采分点。\n3 用户输入中还包含3个评分助理提供给你的不同的打分和评分依据。\n4 理解用户的对话，然后思考并对比这几个打分。选择更合理的得分和得分结果。并重新对用户输出评分和评分依据，评分依据需要言简意赅。\n\n# 要求\n输出的结果为JSON字典，包含问题、回答、评分和评分依据，同时把值写入。\n## 禁止\n不要提及评分助理\n',
      autonomyWithTool: false,
      concurrency: '串行',
      instruments: ['问答详细数据', '打分列表'],
      effects: ['最终得分列表'],
    },
    {
      name: '清空打分列表',
      autonomyAction: '清空列表',
      concurrency: '递归',
      effects: ['打分列表'],
    },
    {
      name: '计算总分',
      autonomyAction: '调用提示词输出对象列表',
      autonomyPrompt:
        '将用户输入中的“最终得分列表”的值提取为一个列表，然后把列表中评分字段的数值累加，写入总分字段，总分字段作为当前列表的最后一项。输出的结果为JSON列表。',
      autonomyWithTool: false,
      concurrency: '串行',
      instrumentConditions: { 判断结果: '结束' },
      instruments: ['最终得分列表'],
      constructions: ['总分和每项得分'],
    },
  ],
} as const;

const MEDDICC_QUESTION = `[
  {
    "问题": "你是在什么时间、什么场景、以什么形式（会议 / 邮件 / 口头）向客户同步这些指标的？",
    "回答": "在上周现场拜访，以口头交流，向业务和IT同时确认的"
  },
  {
    "问题": "这些指标是谁提出、谁定义的？是你自己定的，还是和客户一起确认的？",
    "回答": "共同确认的，我有提出一些备选，客户从中选择，并加了自己的一条指标"
  },
  {
    "问题": "你具体向客户分享了哪些 METRIC 指标？请逐一说明。",
    "回答": "两个核心指标：1.人工时节省：当前基准3小时/单，目标≤1小时/单，测量方式取10个典型测试案例的平均耗时，年报价量约5000单，按专家时薪800元算，年节省价值约800万。2.报价质量（一次通过率）：当前基准70%，目标≥95%，测量方式是统计最终无需返工的报价占比。"
  },
  {
    "问题": "除了你本人，客户方还有谁知道并同意这些指标？是否有更高层级确认？",
    "回答": "业务部负责人张总、IT部李总监、报价组王组长都参与了现场对齐会。第二天张总把指标清单转发给了他们VP陈总，陈总回复“可以，按这个验收”。所以是从执行层到VP层三级都确认过的。"
  },
  {
    "问题": "客户当时具体说了什么、做了什么，让你判断他支持这些指标？请引用原话或行为。",
    "回答": "客户原话是说项目完成后，用这些指标来测量，比如组织几个测试项目，由人类专家和agent+专家来做对比。"
  },
  {
    "问题": "客户有没有提出异议、修改建议或补充指标？你是如何处理并达成一致的？",
    "回答": "客户从备选目标中挑了一个重要的，然后补充了一个新的，我们共同定义了指标和计算方式，但benchmark是口头设定，也许有水分。"
  },
  {
    "问题": "如果现在让你拿出证据证明客户支持这些 METRIC，你能提供什么（记录、邮件、纪要等）？",
    "回答": "1.会议纪要：上周三现场对齐会的会议纪要，有张总、李总监、王组长三方签字，明确记录了指标定义、基准值、目标值、测量方式。2.邮件确认：对齐会当天我发的邮件，第二天李总监回复“同意按此衡量”，张总转发VP陈总，陈总回复“可以，按这个验收”。3.OKR截图：李总监把指标写进部门OKR的系统截图，可以证明客户内部已作为正式目标管理。4.测试计划草案：张总让王组长拟的《报价效率对比测试方案》初稿，里面明确引用了这两个指标作为验收标准。"
  }
]`;

const EXPERIMENT_REPORT_PROCESS = {
  name: '实验报告结果校验',
} as const;

const EXPERIMENT_REPORT_QUESTION = `# 一、溶液标准与实际配制过程
| 溶液名称 | 化学物质代号 | 浓度标准 | 配制过程 | 标识 |
|--------|--------------|----------|----------|------|
| 工作对照品溶液 | XR77312-API | 0.5 mg/mL | 制备两份平行样。<br>1. 精密称取XR77312-API工作对照品约50 mg，置于100 mL量瓶中；<br>2. 加入约60 mL稀释剂，超声处理10分钟使其完全溶解；<br>3. 待冷却至室温后，加稀释剂定容至刻度，摇匀。 | STD-A<br>STD-B |
| 定量限溶液 | XR77312-API | 0.5 μg/mL | 单独配制一份。<br>1. 精密量取1.0 mL STD-A 溶液置于25 mL量瓶中，用稀释剂定容至刻度并摇匀（中间液）；<br>2. 随后精密量取1.0 mL 该中间液置于40 mL量瓶中；<br>3. 再次用稀释剂定容至刻度，充分混匀。 | LOQ |

# 二、方法预验证标准与实测结果
| 验证项目 | 指标项 | 指标要求（接受标准） | 实测结果 |
|----------|--------|----------------------|----------|
| 系统适用性与专属性 | 主成分与相邻色谱峰分离度 | ≥1.5 | 适用性溶液中，主峰与相邻峰的最小分离度为 2.8 |
| 系统适用性与专属性 | 已知杂质峰与其相邻峰分离度 | ≥1.2 | 目标杂质峰的最小分离度为 1.9 |
| 系统适用性与专属性 | 穿插进样标准品溶液回收率 | 98.0%～102.0% | 穿插进样回收率为 99.5% - 100.2% |
| 系统适用性与专属性 | STD-B复核对照溶液准确度 | 98.0%～102.0% | STD-B 复核比对回收率为 99.8% |
| 定量限 | 连续3针LOQ主峰信噪比S/N | ≥10 | 3 针 LOQ 溶液的主峰信噪比分布于 89.2 至 94.1 之间 |
| 定量限 | 连续3针LOQ主峰面积RSD | ≤20.0% | 峰面积计算 RSD 为 4.5% |`;

type ScenarioTaskMockPresetEnv = 'dev' | 'test';

type MockPresetConfig = Partial<
  Pick<
    MockPreset,
    | 'scenarioOntologyId'
    | 'scenarioOntologyName'
    | 'objectOntologyId'
    | 'objectOntologyName'
    | 'knowledgeBaseId'
    | 'knowledgeBaseName'
  >
>;

interface ScenarioTaskMockPresetGlobalConfig {
  env?: string;
  scenarioTaskMockPresetConfigs?: Partial<
    Record<
      ScenarioTaskMockPresetEnv,
      Partial<Record<MockPresetKey, MockPresetConfig>>
    >
  >;
}

const DEFAULT_SCENARIO_TASK_MOCK_PRESET_CONFIGS: Record<
  ScenarioTaskMockPresetEnv,
  Partial<Record<MockPresetKey, MockPresetConfig>>
> = {
  dev: {
    story: {
      scenarioOntologyId: 178,
      scenarioOntologyName: '编写场景故事元图版',
      objectOntologyId: 45,
      objectOntologyName: '座椅总成对象',
      knowledgeBaseName: 'BOM结构-请不要修改',
    },
    meddicc: {
      scenarioOntologyId: null,
      scenarioOntologyName: 'MEDDICC商机评分元图版',
      objectOntologyId: null,
      objectOntologyName: 'MEDDICC评分标准元图版',
    },
    experimentReport: {
      scenarioOntologyId: 184,
    },
  },
  test: {
    story: {
      scenarioOntologyId: 199,
      scenarioOntologyName: '编写场景故事元图版',
      objectOntologyId: 66,
      objectOntologyName: '座椅总成对象',
      knowledgeBaseName: 'BOM结构-请不要修改',
    },
    meddicc: {
      scenarioOntologyId: null,
      scenarioOntologyName: '',
      objectOntologyId: null,
      objectOntologyName: '',
    },
    experimentReport: {
      scenarioOntologyId: 205,
    },
  },
};

export const MOCK_PRESETS: MockPreset[] = [
  {
    key: 'story',
    label: '编写场景故事模拟数据',
    // scenarioOntologyId: 178,
    scenarioOntologyName: '编写场景故事元图版',
    // objectOntologyId: 45,
    objectOntologyName: '座椅总成对象',
    knowledgeBaseName: 'BOM结构-请不要修改',
    variables: [
      { key: '最小资料数量', type: 'number', value: 5 },
      { key: '每次收集条数', type: 'number', value: 5 },
    ],
    process: STORY_PROCESS,
    question:
      '编写一个汽车制造行业企业的AI场景故事，以延锋汽车为故事模版，时间点是未来的2030年，在AI辅助下给座椅总成减轻重量的故事，文中需要包含 零件重量范围、零件BOM结构',
  },
  {
    key: 'meddicc',
    label: 'MEDDICC商机评分模拟数据',
    scenarioOntologyName: 'MEDDICC商机评分元图版',
    objectOntologyName: 'MEDDICC评分标准元图版',
    variables: [
      { key: '客户名称', type: 'string', value: '普洛药业' },
      { key: '项目名称', type: 'string', value: '智能报价' },
    ],
    process: MEDDICC_PROCESS,
    question: MEDDICC_QUESTION,
  },
  {
    key: 'experimentReport',
    label: '实验报告结果校验',
    scenarioOntologyId: 184,
    variables: [],
    process: EXPERIMENT_REPORT_PROCESS,
    question: EXPERIMENT_REPORT_QUESTION,
  },
];

export function getConfiguredMockPreset(
  presetKey: MockPresetKey
): MockPreset | undefined {
  const preset = MOCK_PRESETS.find(item => item.key === presetKey);
  if (!preset) return undefined;

  const config = getMockPresetConfig(presetKey);

  return {
    ...preset,
    ...config,
  };
}

function getMockPresetConfig(presetKey: MockPresetKey): MockPresetConfig {
  const { globalConfig } = useConfig();
  const typedGlobalConfig = globalConfig as ScenarioTaskMockPresetGlobalConfig;
  const env: ScenarioTaskMockPresetEnv =
    typedGlobalConfig.env === 'test' ? 'test' : 'dev';
  const presetConfigs = typedGlobalConfig.scenarioTaskMockPresetConfigs;

  return (
    presetConfigs?.[env]?.[presetKey] ||
    presetConfigs?.dev?.[presetKey] ||
    DEFAULT_SCENARIO_TASK_MOCK_PRESET_CONFIGS[env][presetKey] ||
    {}
  );
}
